import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { Common, WebStorage, WebStorageNames } from "../../../utils";
import "../documents/Documents.css";
import "./TimeSheets.css";
import {
  addDays,
  formatRangeLabel,
  getTimesheetNavWeekStartDate,
  getWeekStartKey,
  getWeekStartMonday,
  parseLocalWeekStartKey,
} from "./timesheetDateUtils";
import { TimeSheetApprovalStatus, TimeSheetStatus } from "./timesheetConstants";
import TimeSheetWeeklyEntryForm, { SUBMIT_ACTION, WEEKLY_STATUS_BANNER } from "./TimeSheetWeeklyEntryForm";
import {
  logTimeSheet,
  submitTimeSheetForApproval,
  getTimeSheetIdFromLogResponse,
  getTimeSheetIdFromWeekly,
  handleTimesheetApiError,
  fetchWeeklyTimeSheet,
  fetchMyOfficeOffDays,
  unwrapWeeklyPayload,
  mapWeeklyProjects,
  getTimeSheetStatusFromWeekly,
  getTimeSheetFromWeekly,
  buildRowsFromTimeSheetEntries,
  getTimeSheetApprovalStatusFromWeekly,
  unsubmitTimeSheet,
} from "./timesheetApi";
import { normalizeTimesheetUserId, subscribeTimesheetRealtime } from "./timesheetRealtimeBus";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";
import { resolveTimelogProjectSituation } from "./timesheetProjectGuidance";
import TimeLogProjectsGuidanceModal from "./TimeLogProjectsGuidanceModal";

const parseWeekFromState = (location) => {
  const fromKey = parseLocalWeekStartKey(location?.state?.weekStartKey);
  if (fromKey) return fromKey;

  const raw = location?.state?.weekStart;
  if (raw == null || raw === "") return null;
  return getTimesheetNavWeekStartDate(raw);
};

const TimeSheetEntryPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { showWorkplaceRequired, workplaceRequiredModal, hasWorkplaces, workplacesLoading } =
    useWorkplaceGate("timesheets");
  /** Edit flow passes `timeSheetId` in location state (URL stays `/timesheets/time-log/edit` without id). */
  const isEdit = Boolean(location.state?.timeSheetId);

  const [weekStart, setWeekStart] = useState(() => parseWeekFromState(location) || getWeekStartMonday(new Date()));
  /** null | 'draft' | 'submit' — drives loading spinners on the matching action */
  const [savingMode, setSavingMode] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [weeklyRefreshKey, setWeeklyRefreshKey] = useState(0);
  const [projectsError, setProjectsError] = useState(null);
  /** undefined while loading; empty when all days are business days (API). */
  const [officeOffDays, setOfficeOffDays] = useState(undefined);
  const [projectsGuidanceOpen, setProjectsGuidanceOpen] = useState(false);
  const [projectsGuidanceSituation, setProjectsGuidanceSituation] = useState(null);
  const [projectsGuidanceChecked, setProjectsGuidanceChecked] = useState(false);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const rangeLabel = formatRangeLabel(weekStart, weekEnd);

  const dayDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  /** Same route component is reused when switching e.g. edit/id1 → edit/id2; state must re-apply. */
  useEffect(() => {
    const parsed = parseWeekFromState(location);
    if (parsed) setWeekStart(parsed);
  }, [location.key, location.state?.weekStartKey, location.state?.timeSheetId]);

  useEffect(() => {
    if (!workplacesLoading && !hasWorkplaces) {
      showWorkplaceRequired();
    }
  }, [workplacesLoading, hasWorkplaces, showWorkplaceRequired]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWeeklyLoading(true);
      setProjectsError(null);
      try {
        const data = await fetchWeeklyTimeSheet(weekStart);
        if (!cancelled) setWeekly(unwrapWeeklyPayload(data));
      } catch (e) {
        if (!cancelled) {
          setWeekly(null);
          setProjectsError(t("app.timesheets.entry.weeklyLoadFailed"));
          handleTimesheetApiError(e, t("app.timesheets.entry.weeklyLoadFailed"));
        }
      } finally {
        if (!cancelled) setWeeklyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekStart, t, weeklyRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const days = await fetchMyOfficeOffDays();
        if (!cancelled) setOfficeOffDays(days);
      } catch {
        if (!cancelled) setOfficeOffDays([0, 6]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribeTimesheetRealtime((data) => {
      const auth = WebStorage.getLocalStore(WebStorageNames.AuthInfo);
      if (
        normalizeTimesheetUserId(data?.employeeUserId) !== normalizeTimesheetUserId(auth?.selfUserId)
      ) {
        return;
      }
      if (String(data.weekStartDate) !== getWeekStartKey(weekStart)) return;
      setWeeklyRefreshKey((k) => k + 1);
    });
  }, [weekStart]);

  const projects = useMemo(() => mapWeeklyProjects(weekly), [weekly]);
  const projectLookup = useMemo(() => new Map(projects.map((p) => [String(p.id), p])), [projects]);

  useEffect(() => {
    if (isEdit || weeklyLoading) return;
    if (projects.length > 0) {
      setProjectsGuidanceOpen(false);
      setProjectsGuidanceChecked(true);
      return;
    }
    if (projectsGuidanceChecked) return;

    let cancelled = false;
    (async () => {
      try {
        const situation = await resolveTimelogProjectSituation();
        if (cancelled) return;
        setProjectsGuidanceSituation(situation);
        setProjectsGuidanceChecked(true);
        if (situation.kind !== "ok") {
          setProjectsGuidanceOpen(true);
        }
      } catch {
        if (!cancelled) {
          setProjectsGuidanceSituation({ kind: "no_projects" });
          setProjectsGuidanceChecked(true);
          setProjectsGuidanceOpen(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, weeklyLoading, projects.length, projectsGuidanceChecked]);

  useEffect(() => {
    setProjectsGuidanceChecked(false);
    setProjectsGuidanceOpen(false);
    setProjectsGuidanceSituation(null);
  }, [location.key]);

  const initialRows = useMemo(() => {
    if (!weekly) return [];
    const ts = getTimeSheetFromWeekly(weekly);
    const ent = ts?.userTimeSheetEntries ?? ts?.UserTimeSheetEntries ?? [];
    return buildRowsFromTimeSheetEntries(ent, dayDates, projectLookup);
  }, [weekly, dayDates, projectLookup]);

  const timeSheetStatus = useMemo(() => getTimeSheetStatusFromWeekly(weekly), [weekly]);
  const approvalStatus = useMemo(() => getTimeSheetApprovalStatusFromWeekly(weekly), [weekly]);
  /** Terminal / locked workflow: same read-only grid as approved (rejected cannot be re-submitted). */
  const readOnly =
    timeSheetStatus === TimeSheetStatus.Approved || timeSheetStatus === TimeSheetStatus.Rejected;

  /** Primary button: submit vs withdraw vs re-submit after return / approver action vs blocked when timesheet is rejected */
  const submitActionKind = useMemo(() => {
    if (readOnly) {
      return timeSheetStatus === TimeSheetStatus.Rejected ? SUBMIT_ACTION.SUBMIT_BLOCKED : SUBMIT_ACTION.SUBMIT;
    }
    if (timeSheetStatus === TimeSheetStatus.Returned) return SUBMIT_ACTION.RESUBMIT;
    if (timeSheetStatus === TimeSheetStatus.Draft) return SUBMIT_ACTION.SUBMIT;
    if (timeSheetStatus === TimeSheetStatus.Submitted) {
      if (approvalStatus === TimeSheetApprovalStatus.Rejected || approvalStatus === TimeSheetApprovalStatus.Returned) {
        return SUBMIT_ACTION.RESUBMIT;
      }
      return SUBMIT_ACTION.UNSUBMIT;
    }
    return SUBMIT_ACTION.SUBMIT;
  }, [readOnly, timeSheetStatus, approvalStatus]);

  /** Colored status strip above the grid (Approved, Rejected, Returned, Submitted). */
  const weeklyStatusBannerKind = useMemo(() => {
    if (weeklyLoading) return null;
    if (timeSheetStatus === TimeSheetStatus.Approved) return WEEKLY_STATUS_BANNER.APPROVED;
    if (timeSheetStatus === TimeSheetStatus.Rejected) return WEEKLY_STATUS_BANNER.REJECTED_BLOCKED;
    if (timeSheetStatus === TimeSheetStatus.Returned) return WEEKLY_STATUS_BANNER.RETURNED;
    if (timeSheetStatus === TimeSheetStatus.Submitted) {
      if (approvalStatus === TimeSheetApprovalStatus.Returned) return WEEKLY_STATUS_BANNER.RETURNED;
      if (approvalStatus === TimeSheetApprovalStatus.Rejected) return WEEKLY_STATUS_BANNER.REJECTED_RESUBMIT;
      return WEEKLY_STATUS_BANNER.SUBMITTED;
    }
    return null;
  }, [weeklyLoading, timeSheetStatus, approvalStatus]);

  const breadcrumbSegment = useMemo(() => {
    if (weeklyLoading) return t("app.timesheets.entry.breadcrumbLoading");
    if (timeSheetStatus == null) return t("app.timesheets.entry.breadcrumbNew");
    switch (timeSheetStatus) {
      case TimeSheetStatus.Draft:
        return t("app.timesheets.entry.breadcrumbDraft");
      case TimeSheetStatus.Submitted:
        return t("app.timesheets.entry.breadcrumbEditSubmitted");
      case TimeSheetStatus.Approved:
        return t("app.timesheets.entry.breadcrumbApproved");
      case TimeSheetStatus.Rejected:
        return t("app.timesheets.entry.breadcrumbRejected");
      case TimeSheetStatus.Returned:
        return t("app.timesheets.entry.breadcrumbReturned");
      default:
        return t("app.timesheets.entry.breadcrumbNew");
    }
  }, [weeklyLoading, timeSheetStatus, t]);

  const pageTitle = useMemo(() => {
    if (readOnly) return t("app.timesheets.entry.viewTitle");
    if (timeSheetStatus === TimeSheetStatus.Submitted) return t("app.timesheets.entry.editSubmittedTitle");
    if (timeSheetStatus === TimeSheetStatus.Draft || timeSheetStatus === TimeSheetStatus.Returned) {
      return t("app.timesheets.entry.editTitle");
    }
    return isEdit ? t("app.timesheets.entry.editTitle") : t("app.timesheets.entry.createTitle");
  }, [readOnly, timeSheetStatus, isEdit, t]);

  const isViewingCurrentWeek = useMemo(() => {
    const todayWeekStart = getWeekStartMonday(new Date());
    return (
      weekStart.getFullYear() === todayWeekStart.getFullYear() &&
      weekStart.getMonth() === todayWeekStart.getMonth() &&
      weekStart.getDate() === todayWeekStart.getDate()
    );
  }, [weekStart]);

  const goToCurrentWeek = () => {
    setWeekStart(getWeekStartMonday(new Date()));
  };

  const goBack = () => history.push("/timesheets/time-log");

  const handleSaveDraft = async (requestBody) => {
    if (readOnly) return;
    setSavingMode("draft");
    try {
      await logTimeSheet(requestBody);
      Common.showSuccessMessage(t("app.timesheets.entry.draftSaved"));
      goBack();
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.entry.saveFailed"));
    } finally {
      setSavingMode(null);
    }
  };

  const handleSubmit = async (requestBody) => {
    if (readOnly || submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED) return;
    setSavingMode("submit");
    try {
      const data = await logTimeSheet(requestBody);
      const fromResponse = getTimeSheetIdFromLogResponse(data);
      const existing = getTimeSheetIdFromWeekly(weekly);
      const timeSheetId = fromResponse ?? existing;
      if (!timeSheetId) {
        Common.showErrorMessage(t("app.timesheets.entry.missingTimesheetId"));
        return;
      }
      await submitTimeSheetForApproval(timeSheetId);
      Common.showSuccessMessage(
        submitActionKind === SUBMIT_ACTION.RESUBMIT ? t("app.timesheets.entry.resubmitSuccess") : t("app.timesheets.entry.submitted")
      );
      goBack();
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.entry.submitFailed"));
    } finally {
      setSavingMode(null);
    }
  };

  const handleUnsubmit = async () => {
    if (readOnly) return;
    setSavingMode("unsubmit");
    try {
      const timeSheetId = getTimeSheetIdFromWeekly(weekly);
      if (!timeSheetId) {
        Common.showErrorMessage(t("app.timesheets.entry.missingTimesheetId"));
        return;
      }
      await unsubmitTimeSheet(timeSheetId);
      Common.showSuccessMessage(t("app.timesheets.entry.unsubmitSuccess"));
      const data = await fetchWeeklyTimeSheet(weekStart);
      setWeekly(unwrapWeeklyPayload(data));
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.entry.unsubmitFailed"));
    } finally {
      setSavingMode(null);
    }
  };

  const shiftWeek = (delta) => {
    setWeekStart((w) => addDays(w, delta * 7));
  };

  return (
    <div className="documents-page ts-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-calendar-week" />}
        title={pageTitle}
        description={t("app.timesheets.entry.pageDescription")}
        onBack={goBack}
        backLabel={t("app.timesheets.entry.backToTimeLog")}
      />

      <div className="ts-entry-top-bar">
        <nav className="ts-entry-breadcrumb ts-entry-breadcrumb--in-top-bar" aria-label={t("app.timesheets.entry.breadcrumbLabel")}>
          <span>{t("app.timesheets.entry.breadcrumbLedger")}</span>
          <i className="bi bi-chevron-right small" aria-hidden />
          <span className="ts-bc-current">{breadcrumbSegment}</span>
        </nav>
        <div className="ts-entry-week-toolbar ts-entry-week-toolbar--in-top-bar">
          <div className="ts-period-cluster ts-period-cluster--inline" aria-label={t("app.timesheets.dashboard.periodLabel")}>
            <button
              type="button"
              onClick={() => shiftWeek(-1)}
              aria-label={t("app.timesheets.common.prevPeriod")}
              disabled={weeklyLoading}
            >
              <i className="bi bi-chevron-left" />
            </button>
            <span className="ts-period-pill ts-period-pill--inline">
              <i className="bi bi-calendar3 text-primary" aria-hidden />
              {rangeLabel}
            </span>
            <button
              type="button"
              onClick={() => shiftWeek(1)}
              aria-label={t("app.timesheets.common.nextPeriod")}
              disabled={weeklyLoading}
            >
              <i className="bi bi-chevron-right" />
            </button>
          </div>
          {!isViewingCurrentWeek ? (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm ts-entry-jump-current-week"
              onClick={goToCurrentWeek}
              aria-label={t("app.timesheets.entry.jumpToCurrentWeekAria")}
              disabled={weeklyLoading}
            >
              <i className="bi bi-calendar-check me-1" aria-hidden />
              {t("app.timesheets.entry.jumpToCurrentWeek")}
            </button>
          ) : null}
          {weeklyLoading ? (
            <span className="d-inline-flex align-items-center ms-1 ms-md-2" role="status" aria-live="polite" title={t("app.timesheets.entry.weeklyLoading")}>
              <span className="spinner-border spinner-border-sm text-primary" aria-hidden />
              <span className="visually-hidden">{t("app.timesheets.entry.weeklyLoading")}</span>
            </span>
          ) : null}
        </div>
      </div>

      <TimeSheetWeeklyEntryForm
        weekStart={weekStart}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
        onUnsubmit={handleUnsubmit}
        submitActionKind={submitActionKind}
        savingMode={savingMode}
        projects={projects}
        projectsLoading={weeklyLoading}
        projectsError={projectsError}
        initialRows={initialRows}
        weeklyLoading={weeklyLoading}
        readOnly={readOnly}
        statusBannerKind={weeklyStatusBannerKind}
        officeOffDays={officeOffDays}
      />

      {workplaceRequiredModal}
      <TimeLogProjectsGuidanceModal
        open={projectsGuidanceOpen}
        onClose={() => setProjectsGuidanceOpen(false)}
        situation={projectsGuidanceSituation}
      />
    </div>
  );
};

export default TimeSheetEntryPage;
